// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — the rules, as a pure state machine over typed arrays. No DOM, no clock, no network:
// every peer, the bot's search, the balance sim and the mechanism audit all step the same code.
//
// A turn is ONE ply: move a huntress along a clear queen line, then fire an arrow from where she
// landed along another clear queen line. The arrow's square burns for good. The square she LEFT is
// empty behind her, so it is a legal arrow target — that is not an edge case, it is the manoeuvre
// that makes the game (retreat and seal the door you came through).
//
// TERMINATION IS STRUCTURAL, not a hope: every ply burns exactly one empty square and nothing ever
// un-burns, so the game cannot last more than `emptyAtStart` plies. That is what makes the ply cap
// an arithmetic bound rather than a guess, and it is asserted in tests/rules.test.ts.

import type { Mode } from './modes';
import { modeOf } from './modes';

export const RULES_REV = 1;

/** Cell contents. Non-negative values are a huntress id. */
export const EMPTY = -1;
export const BURNT = -2;

/** Ply tag. Only one ply shape exists; the two tag bits are reserved. */
export const T_MOVE = 0;

export type WinReason = 'sealed' | 'territory' | 'walkover' | 'nocontest' | 'none';

export interface SeatStats {
  /** Turns actually taken. */
  turns: number;
  /** Sum of the mover's legal-ply count at the start of each of their turns, and the count. */
  mobilitySum: number;
  mobilityN: number;
  /** Total distance walked, in squares. */
  steps: number;
  /** Arrows that landed on a square adjacent (king-wise) to an enemy huntress. */
  pins: number;
  /** Arrows fired into the square the huntress had just vacated. */
  doors: number;
  /** Territory owned at the end — squares this side reaches strictly sooner (queen distance). */
  land: number;
}

export interface PlyEvent {
  kind: 'move' | 'burn' | 'seal' | 'territory';
  cell?: number;
  from?: number;
  seat?: number;
}

export interface State {
  mode: Mode;
  w: number;
  h: number;
  cells: number;
  /** Huntresses per side. */
  k: number;
  cell: Int8Array;
  /** Cell index of each huntress: 0..k-1 are side 0, k..2k-1 are side 1. */
  pos: Int8Array;
  /** Side to move. */
  turn: number;
  ply: number;
  winner: number;
  reason: WinReason;
  log: number[];
  seed: number;
  stats: [SeatStats, SeatStats];
}

// ── ply packing ────────────────────────────────────────────────────────────────
// from(6) | to(6) | arrow(6) | tag(2) — 20 bits, so a whole game's log is a handful of small ints
// on the wire. Six bits caps the board at 64 cells, which is exactly the 8x8 ceiling MOBILE_CONTROLS
// allows at a 44px touch target.
export const packPly = (from: number, to: number, arrow: number): number =>
  ((from & 63) | ((to & 63) << 6) | ((arrow & 63) << 12)) >>> 0;
export const plyFrom = (p: number): number => p & 63;
export const plyTo = (p: number): number => (p >>> 6) & 63;
export const plyArrow = (p: number): number => (p >>> 12) & 63;
export const plyTag = (p: number): number => (p >>> 18) & 3;

// ── geometry ───────────────────────────────────────────────────────────────────
const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

export const xOf = (s: State, i: number): number => i % s.w;
export const yOf = (s: State, i: number): number => (i / s.w) | 0;
export const idx = (s: State, x: number, y: number): number => y * s.w + x;
export const inBounds = (s: State, x: number, y: number): boolean => x >= 0 && y >= 0 && x < s.w && y < s.h;

/** Algebraic name for a square — a1 is the bottom-left, as on any board. */
export function cellName(s: State, i: number): string {
  return `${String.fromCharCode(97 + xOf(s, i))}${s.h - yOf(s, i)}`;
}

export const sideOf = (s: State, huntress: number): number => (huntress < s.k ? 0 : 1);
export const huntressesOf = (s: State, side: number): number[] =>
  Array.from({ length: s.k }, (_, i) => side * s.k + i);



// ── setup ──────────────────────────────────────────────────────────────────────

/**
 * THE OPENING IS DRAWN FROM THE SEED AND IS 180°-ROTATIONALLY SYMMETRIC BY CONSTRUCTION.
 *
 * Both halves of that sentence are load-bearing, and the first one was learned from the sim rather
 * than chosen. The traditional fixed opening looked correct — perfectly mirrored, provably fair at
 * turn 0 — and the very first balance run came back with the first player winning 100% of games in
 * two modes out of three. Nothing was imbalanced: a fixed opening plus a deterministic engine is
 * ONE game, replayed, so the "win rate" was a sample of size one wearing a sample of size forty's
 * clothes. It is also, much more importantly, a bad product — a solo player would have got the same
 * board and very nearly the same game every single time they pressed Play.
 *
 * Drawing the opening from the round seed fixes both at once, and symmetry keeps the fairness that
 * the fixed opening was there to provide: cells pair up under 180° rotation, `huntresses` pairs are
 * drawn, and each side takes one of each pair. Rotation is an involution, so whichever member of a
 * pair goes to side 0, rotating the whole position still maps each side exactly onto the other —
 * neither seat can be handed the better half of a board that is its own mirror image.
 */
export function openingFor(mode: Mode, seed: number): number[] {
  const w = mode.w;
  const h = mode.h;
  const cells = w * h;
  const rot = (i: number): number => (h - 1 - ((i / w) | 0)) * w + (w - 1 - (i % w));
  const pairs: Array<[number, number]> = [];
  for (let i = 0; i < cells; i++) {
    const r = rot(i);
    if (r > i) pairs.push([i, r]);
  }

  const kingApart = (a: number, b: number): boolean =>
    Math.max(Math.abs((a % w) - (b % w)), Math.abs(((a / w) | 0) - ((b / w) | 0))) > 1;

  let state = (seed >>> 0) || 1;
  const rand = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  for (let attempt = 0; attempt < 400; attempt++) {
    const pool = pairs.slice();
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    const side0: number[] = [];
    const side1: number[] = [];
    for (const pair of pool) {
      if (side0.length >= mode.huntresses) break;
      const flip = rand() < 0.5;
      const mine = flip ? pair[1] : pair[0];
      const theirs = flip ? pair[0] : pair[1];
      // Nobody starts shoulder to shoulder: a huntress boxed in from move one is a dead piece and
      // the mirror makes it a dead piece for BOTH players, which is fair and also dull. The pair
      // has to be checked against ITSELF too — near the centre a square and its own rotation are
      // neighbours (on 6×6, c3 rotates onto d4).
      if (!kingApart(mine, theirs)) continue;
      if ([...side0, ...side1].some((c) => !kingApart(c, mine) || !kingApart(c, theirs))) continue;
      side0.push(mine);
      side1.push(theirs);
    }
    if (side0.length === mode.huntresses) return [...side0, ...side1];
  }
  return mode.fallback.slice();
}

export function createState(modeId: string, seed = 1): State {
  const mode = modeOf(modeId);
  const cells = mode.w * mode.h;
  const s: State = {
    mode,
    w: mode.w,
    h: mode.h,
    cells,
    k: mode.huntresses,
    cell: new Int8Array(cells).fill(EMPTY),
    pos: new Int8Array(mode.huntresses * 2).fill(-1),
    turn: 0,
    ply: 0,
    winner: -1,
    reason: 'none',
    log: [],
    seed: seed >>> 0,
    stats: [freshStats(), freshStats()],
  };
  openingFor(mode, seed).forEach((cellIndex, i) => {
    s.cell[cellIndex] = i;
    s.pos[i] = cellIndex;
  });
  return s;
}

function freshStats(): SeatStats {
  return { turns: 0, mobilitySum: 0, mobilityN: 0, steps: 0, pins: 0, doors: 0, land: 0 };
}

export function cloneState(s: State): State {
  return {
    ...s,
    cell: new Int8Array(s.cell),
    pos: new Int8Array(s.pos),
    log: s.log.slice(),
    stats: [{ ...s.stats[0] }, { ...s.stats[1] }],
  };
}

/**
 * The exact upper bound on how long a game can run. Every ply burns one empty square and no square
 * ever un-burns, so this is arithmetic — not a safety valve that might be hit.
 */
export const maxPlies = (s: State): number => s.cells - s.k * 2;

export const isOver = (s: State): boolean => s.winner !== -1 || s.reason !== 'none';

// ── move generation ────────────────────────────────────────────────────────────

/**
 * Every square a queen on `from` can reach, with `ignore` treated as empty. `ignore` is how the
 * arrow step sees the square the huntress just left: she is no longer standing there, so it is both
 * passable and a legal target.
 */
export function rayTargets(s: State, from: number, ignore: number, out: number[]): number[] {
  const x0 = xOf(s, from);
  const y0 = yOf(s, from);
  for (const [dx, dy] of DIRS) {
    let x = x0 + dx;
    let y = y0 + dy;
    while (inBounds(s, x, y)) {
      const i = idx(s, x, y);
      if (s.cell[i] !== EMPTY && i !== ignore) break;
      out.push(i);
      x += dx;
      y += dy;
    }
  }
  return out;
}

/** Just the count — the eval's mobility term calls this in the inner loop and allocates nothing. */
export function rayCount(s: State, from: number, ignore: number): number {
  const x0 = xOf(s, from);
  const y0 = yOf(s, from);
  let n = 0;
  for (const [dx, dy] of DIRS) {
    let x = x0 + dx;
    let y = y0 + dy;
    while (inBounds(s, x, y)) {
      const i = idx(s, x, y);
      if (s.cell[i] !== EMPTY && i !== ignore) break;
      n++;
      x += dx;
      y += dy;
    }
  }
  return n;
}

/**
 * Every legal ply for the side to move. This is the ONE definition of legality in the codebase —
 * the UI filters this list rather than re-deriving it, the bot searches it, and a ply arriving over
 * the wire is checked against it. The mechanism audit deliberately does NOT use it (it re-derives
 * legality from the geometry, or the audit would be checking the generator against itself).
 */
export function genPlies(s: State): number[] {
  const out: number[] = [];
  if (isOver(s)) return out;
  const dests: number[] = [];
  const shots: number[] = [];
  for (const hu of huntressesOf(s, s.turn)) {
    const from = s.pos[hu];
    if (from < 0) continue;
    dests.length = 0;
    rayTargets(s, from, -1, dests);
    for (const to of dests) {
      shots.length = 0;
      // `from` is vacated the instant she moves, so it is passable AND shootable.
      s.cell[from] = EMPTY;
      s.cell[to] = hu;
      rayTargets(s, to, -1, shots);
      s.cell[to] = EMPTY;
      s.cell[from] = hu;
      for (const arrow of shots) out.push(packPly(from, to, arrow));
    }
  }
  return out;
}

/** Whether the side to move has anything at all. Cheaper than generating the full list. */
export function hasMove(s: State, side: number): boolean {
  for (const hu of huntressesOf(s, side)) {
    const from = s.pos[hu];
    if (from >= 0 && rayCount(s, from, -1) > 0) return true;
  }
  return false;
}

/** Legal-ply count without materialising them — the mobility statistic and the sim both want it. */
export function countPlies(s: State): number {
  let total = 0;
  const dests: number[] = [];
  for (const hu of huntressesOf(s, s.turn)) {
    const from = s.pos[hu];
    if (from < 0) continue;
    dests.length = 0;
    rayTargets(s, from, -1, dests);
    for (const to of dests) {
      s.cell[from] = EMPTY;
      s.cell[to] = hu;
      total += rayCount(s, to, -1);
      s.cell[to] = EMPTY;
      s.cell[from] = hu;
    }
  }
  return total;
}

// ── stepping ───────────────────────────────────────────────────────────────────

function kingAdjacentToEnemy(s: State, cellIndex: number, side: number): boolean {
  const x0 = xOf(s, cellIndex);
  const y0 = yOf(s, cellIndex);
  for (const [dx, dy] of DIRS) {
    const x = x0 + dx;
    const y = y0 + dy;
    if (!inBounds(s, x, y)) continue;
    const v = s.cell[idx(s, x, y)];
    if (v >= 0 && sideOf(s, v) !== side) return true;
  }
  return false;
}

/**
 * Apply one ply. `validate` is off inside the search (the plies come from genPlies by construction)
 * and on everywhere a ply can arrive from outside this process. `adjudicating` is off inside the
 * search too: the early verdict costs two component floods and two playouts, which is nothing once
 * a turn and ruinous once a node.
 */
export function applyPly(s: State, ply: number, events?: PlyEvent[], validate = true, adjudicating = true): boolean {
  if (isOver(s)) return false;
  const from = plyFrom(ply);
  const to = plyTo(ply);
  const arrow = plyArrow(ply);
  const hu = s.cell[from];
  if (validate) {
    if (hu < 0 || sideOf(s, hu) !== s.turn) return false;
    if (!genPlies(s).includes(ply >>> 0)) return false;
  }

  const side = s.turn;
  const st = s.stats[side];
  st.mobilitySum += countPlies(s);
  st.mobilityN++;
  st.steps += Math.max(Math.abs(xOf(s, to) - xOf(s, from)), Math.abs(yOf(s, to) - yOf(s, from)));

  s.cell[from] = EMPTY;
  s.cell[to] = hu;
  s.pos[hu] = to;
  s.cell[arrow] = BURNT;
  if (arrow === from) st.doors++;
  if (kingAdjacentToEnemy(s, arrow, side)) st.pins++;
  st.turns++;
  s.log.push(ply >>> 0);
  s.ply++;
  s.turn = 1 - side;

  events?.push({ kind: 'move', from, cell: to, seat: side });
  events?.push({ kind: 'burn', cell: arrow, seat: side });

  settleEnd(s, events, adjudicating);
  return true;
}

export const step = applyPly;

function settleEnd(s: State, events: PlyEvent[] | undefined, adjudicating: boolean): void {
  if (isOver(s)) return;
  if (!hasMove(s, s.turn)) {
    s.winner = 1 - s.turn;
    s.reason = 'sealed';
    events?.push({ kind: 'seal', seat: s.winner });
    return;
  }
  if (!adjudicating) return;
  const verdict = adjudicate(s);
  if (verdict !== null) {
    s.winner = verdict;
    s.reason = 'territory';
    events?.push({ kind: 'territory', seat: verdict });
  }
}

// ── early adjudication ─────────────────────────────────────────────────────────

/**
 * Once the two sides can no longer touch each other, the rest of the game is two solitaires: each
 * side burns its own room down and whoever runs out of moves first loses. Playing that out is a
 * dozen taps of pure bookkeeping, so it is adjudicated — but ONLY when the result is a fact.
 *
 * THE SEPARATION TEST IS THE LOAD-BEARING PART, and the obvious version of it is wrong. "Neither
 * side can currently reach a square the other can" is NOT enough: an enemy huntress standing in a
 * doorway blocks a ray, and the moment she steps aside the ray opens and one side's room grows. An
 * upper bound derived from a reach that can grow is not an upper bound, and this adjudicator would
 * then hand out a wrong result in a position nobody had actually lost — a silent correctness bug a
 * balance sim would happily average away.
 *
 * So separation is computed with EVERY huntress transparent and only burnt squares blocking:
 * `component()` is a connected component of the not-yet-burnt squares under queen-ray adjacency.
 * Burning only ever REMOVES squares, so two cells in different components can never become
 * connected, a huntress can never leave her component, and an arrow can never land outside it. The
 * two solitaires are then genuinely independent.
 *
 * Given that, the bounds are exact rather than heuristic:
 *
 *   * Every turn a side takes changes its component's empty count by exactly −1 (she vacates one
 *     square, occupies another, and burns a third). So UPPER = the empty squares in her component,
 *     full stop.
 *   * LOWER = what a fixed filling policy actually achieves when played out, which she can always
 *     copy, so it is achievable by construction.
 *
 * Then, since the sides strictly alternate: with X to move, X outlasts Y iff LOWER(X) > UPPER(Y);
 * with Y to move, X needs only LOWER(X) >= UPPER(Y), because Y has to find a move first. If the
 * bounds do not separate, nothing is declared and the players simply play on.
 */
export function component(s: State, side: number): Uint8Array {
  const seen = new Uint8Array(s.cells);
  const queue: number[] = [];
  for (const hu of huntressesOf(s, side)) {
    const at = s.pos[hu];
    if (at >= 0 && !seen[at]) {
      seen[at] = 1;
      queue.push(at);
    }
  }
  while (queue.length) {
    const at = queue.pop()!;
    const x0 = xOf(s, at);
    const y0 = yOf(s, at);
    for (const [dx, dy] of DIRS) {
      let x = x0 + dx;
      let y = y0 + dy;
      while (inBounds(s, x, y)) {
        const i = idx(s, x, y);
        if (s.cell[i] === BURNT) break; // only fire blocks; huntresses are transparent here
        if (!seen[i]) {
          seen[i] = 1;
          queue.push(i);
        }
        x += dx;
        y += dy;
      }
    }
  }
  return seen;
}

export function separated(s: State): boolean {
  const a = component(s, 0);
  const b = component(s, 1);
  for (let i = 0; i < s.cells; i++) if (a[i] && b[i]) return false;
  return true;
}

/** Empty squares inside a component — the exact ceiling on how many turns that side has left. */
function emptyIn(s: State, comp: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < s.cells; i++) if (comp[i] && s.cell[i] === EMPTY) n++;
  return n;
}

/**
 * How many turns a side can definitely still take, playing alone. The policy is "move the shortest
 * distance you can and shoot back into the square you left", which keeps a huntress inside her room
 * and burns it one square at a time; whatever it scores is achievable, so it is a valid lower bound.
 */
export function guaranteedTurns(s: State, side: number, cap = 128, out?: number[]): number {
  const probe = cloneState(s);
  let turns = 0;
  const dests: number[] = [];
  const shots: number[] = [];
  while (turns < cap) {
    let bestPly = -1;
    let bestDist = Infinity;
    for (const hu of huntressesOf(probe, side)) {
      const from = probe.pos[hu];
      if (from < 0) continue;
      dests.length = 0;
      rayTargets(probe, from, -1, dests);
      for (const to of dests) {
        const dist = Math.max(Math.abs(xOf(probe, to) - xOf(probe, from)), Math.abs(yOf(probe, to) - yOf(probe, from)));
        if (dist >= bestDist) continue;
        probe.cell[from] = EMPTY;
        probe.cell[to] = hu;
        shots.length = 0;
        rayTargets(probe, to, -1, shots);
        probe.cell[to] = EMPTY;
        probe.cell[from] = hu;
        if (!shots.length) continue;
        // Step one square and shoot back through the doorway when we can — that burns the room
        // down a square at a time instead of cutting it in half.
        bestPly = packPly(from, to, shots.includes(from) ? from : shots[shots.length - 1]);
        bestDist = dist;
      }
    }
    if (bestPly < 0) break;
    out?.push(bestPly);
    const from = plyFrom(bestPly);
    const to = plyTo(bestPly);
    const arrow = plyArrow(bestPly);
    const hu = probe.cell[from];
    probe.cell[from] = EMPTY;
    probe.cell[to] = hu;
    probe.pos[hu] = to;
    probe.cell[arrow] = BURNT;
    turns++;
  }
  return turns;
}

/** The winner if — and only if — the position is already decided. `null` means play on. */
export function adjudicate(s: State): number | null {
  const a = component(s, 0);
  const b = component(s, 1);
  for (let i = 0; i < s.cells; i++) if (a[i] && b[i]) return null;
  const upper: [number, number] = [emptyIn(s, a), emptyIn(s, b)];
  const lower: [number, number] = [guaranteedTurns(s, 0), guaranteedTurns(s, 1)];
  for (const side of [0, 1]) {
    const other = 1 - side;
    // Strict alternation: the side to move has to produce a move first, so it needs one more.
    const need = upper[other] + (side === s.turn ? 1 : 0);
    if (lower[side] >= need) return side;
  }
  return null;
}

/** Squares each side reaches strictly sooner than the other — the results screen's territory map. */
export function territory(s: State): { own: [number, number]; map: Int8Array } {
  const map = new Int8Array(s.cells).fill(-1);
  const d0 = queenDistance(s, 0);
  const d1 = queenDistance(s, 1);
  const own: [number, number] = [0, 0];
  for (let i = 0; i < s.cells; i++) {
    if (s.cell[i] !== EMPTY) continue;
    if (d0[i] < d1[i]) {
      map[i] = 0;
      own[0]++;
    } else if (d1[i] < d0[i]) {
      map[i] = 1;
      own[1]++;
    }
  }
  return { own, map };
}

const FAR = 99;

/** Queen-move distance from a side's huntresses to every square — the eval's territory term. */
export function queenDistance(s: State, side: number): Int8Array {
  const dist = new Int8Array(s.cells).fill(FAR);
  let frontier: number[] = [];
  for (const hu of huntressesOf(s, side)) {
    const at = s.pos[hu];
    if (at < 0) continue;
    dist[at] = 0;
    frontier.push(at);
  }
  const targets: number[] = [];
  let d = 1;
  while (frontier.length && d < 60) {
    const next: number[] = [];
    for (const at of frontier) {
      targets.length = 0;
      rayTargets(s, at, -1, targets);
      for (const t of targets) {
        if (dist[t] !== FAR) continue;
        dist[t] = d;
        next.push(t);
      }
    }
    frontier = next;
    d++;
  }
  return dist;
}

/** King-move distance — the second half of the standard two-metric territory evaluation. */
export function kingDistance(s: State, side: number): Int8Array {
  const dist = new Int8Array(s.cells).fill(FAR);
  let frontier: number[] = [];
  for (const hu of huntressesOf(s, side)) {
    const at = s.pos[hu];
    if (at < 0) continue;
    dist[at] = 0;
    frontier.push(at);
  }
  let d = 1;
  while (frontier.length && d < 60) {
    const next: number[] = [];
    for (const at of frontier) {
      const x0 = xOf(s, at);
      const y0 = yOf(s, at);
      for (const [dx, dy] of DIRS) {
        const x = x0 + dx;
        const y = y0 + dy;
        if (!inBounds(s, x, y)) continue;
        const t = idx(s, x, y);
        if (s.cell[t] !== EMPTY || dist[t] !== FAR) continue;
        dist[t] = d;
        next.push(t);
      }
    }
    frontier = next;
    d++;
  }
  return dist;
}

export const emptyCells = (s: State): number => {
  let n = 0;
  for (let i = 0; i < s.cells; i++) if (s.cell[i] === EMPTY) n++;
  return n;
};

export const burntCells = (s: State): number => {
  let n = 0;
  for (let i = 0; i < s.cells; i++) if (s.cell[i] === BURNT) n++;
  return n;
};

/** FNV-1a over the position — the lockstep desync check, cheap and order-sensitive. */
export function digest(s: State): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v & 255;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  for (let i = 0; i < s.cells; i++) mix(s.cell[i] + 3);
  mix(s.turn);
  mix(s.ply);
  mix(s.winner + 2);
  return h >>> 0;
}
